import React, { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '#app/hooks/useAppStore';
import { forgot, handleCloseModal, requestForgot } from '../../actions/auth';
import { handleTitle } from '../../actions/interface';
import { Container, GreyText, Text, Wrapper } from './styles';
import { Button, Input, InputContainer } from 'finform';
import { Row } from '#app/pages/styles';
import LocalStorageHelper from '#app/helpers/Cache/LocalStorageHelper';
import { NotifyManager } from 'notify-component';
import { useForgotCodeForm, useForgotEmailForm } from './hooks/useForgotForm';
import {
  shouldShowFieldError,
  getFieldErrorFromMeta,
} from '#app/utils/tanstackFormErrorVisibility';
import type { ValidateFormRefObject } from './hooks/useAuthForm';

export interface ForgotI18n {
  auth: {
    enter: string;
    email?: string;
    codeAdvise: string;
    code: string;
    createNewPassword: string;
    newPassword: string;
    newPasswordRepeat: string;
    issue: string;
    save: string;
    restore: string;
  };
}

export interface ForgotProps {
  callback?: () => void;
}

function Forgot({ callback }: ForgotProps = {}) {
  const dispatch = useAppDispatch();
  const i18n = useAppSelector((s) => s.i18n) as any;
  const handleTitleFn = useCallback(
    (title: string) => dispatch((handleTitle as any)(title)),
    [dispatch]
  );
  const handleCloseModalFn = useCallback(() => dispatch(handleCloseModal() as any), [dispatch]);
  const forgotFn = useCallback((payload: any) => dispatch((forgot as any)(payload)), [dispatch]);
  const requestForgotFn = useCallback(
    (payload: any) => dispatch((requestForgot as any)(payload)),
    [dispatch]
  );

  const initialEmail = localStorage.getItem(LocalStorageHelper.AUTH_EMAIL) || '';
  const [codeStep, setCodeStep] = useState(false);
  const [savedEmail, setSavedEmail] = useState(initialEmail);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    handleTitleFn(i18n.auth.enter);
  }, [handleTitleFn, i18n.auth.enter]);

  const { tanstackForm: emailForm, handleSubmit: handleEmailSubmit } = useForgotEmailForm({
    initialEmail,
    onSubmit: (values, obj) => {
      setSubmitted(true);
      requestForgotFn({
        form: values,
        obj,
        success: () => {
          setSavedEmail(values.email);
          setCodeStep(true);
          setSubmitted(false);
        },
        error: (_xhr: any, responseData?: any) => {
          setSubmitted(false);
          if (!responseData?.meta?.errors && responseData?.meta?.message) {
            NotifyManager.error('Ошибка', responseData.meta.message, 5000);
          }
        },
      });
    },
  });

  const { tanstackForm: codeForm, handleSubmit: handleCodeFormSubmit } = useForgotCodeForm({
    onSubmit: handleForgotCodeSubmit,
  });

  function handleForgotCodeSubmit(values: Record<string, unknown>, obj: ValidateFormRefObject) {
    setSubmitted(true);
    forgotFn({
      form: {
        ...values,
        email: savedEmail,
        form: 'client',
      },
      obj,
      success: () => {
        NotifyManager.info(
          'Успешно',
          'Пароль успешно восстановлен. Вы можете авторизоваться с новым паролем.',
          10000
        );
        handleCloseModalFn();
        codeForm.reset();
        setSubmitted(false);
        setCodeStep(false);
        if (typeof callback === 'function') {
          callback();
        }
      },
      error: (_xhr: any, responseData?: any) => {
        setSubmitted(false);
        if (!responseData?.meta?.errors && responseData?.meta?.message) {
          NotifyManager.error('Ошибка', responseData.meta.message, 5000);
        }
      },
    });
  }

  const handleEnterEmail = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.keyCode === 13) {
        handleEmailSubmit();
      }
    },
    [handleEmailSubmit]
  );

  const handleEnterCode = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.keyCode === 13) {
        handleCodeFormSubmit();
      }
    },
    [handleCodeFormSubmit]
  );

  if (codeStep) {
    return (
      <Wrapper>
        <Container>
          <Text>
            {i18n.auth.codeAdvise} {savedEmail}
          </Text>

          <codeForm.Field name="code">
            {(field) => (
              <Row>
                <InputContainer
                  error={
                    shouldShowFieldError(field.state.meta, codeForm.state)
                      ? getFieldErrorFromMeta(field.state.meta)
                      : undefined
                  }
                >
                  <Input
                    id="forgot-code"
                    type="text"
                    name="code"
                    value={field.state.value}
                    onKeyDown={handleEnterCode}
                    onChange={(value) => field.handleChange(typeof value === 'string' ? value : '')}
                    onBlur={field.handleBlur}
                    placeholder={i18n.auth.code}
                  />
                </InputContainer>
              </Row>
            )}
          </codeForm.Field>

          <h3>{i18n.auth.createNewPassword}</h3>

          <codeForm.Field name="password">
            {(field) => (
              <Row>
                <InputContainer
                  error={
                    shouldShowFieldError(field.state.meta, codeForm.state)
                      ? getFieldErrorFromMeta(field.state.meta)
                      : undefined
                  }
                >
                  <Input
                    id="forgot-password"
                    type="password"
                    name="password"
                    autoComplete="new-password"
                    value={field.state.value}
                    onKeyDown={handleEnterCode}
                    onChange={(value) => field.handleChange(typeof value === 'string' ? value : '')}
                    onBlur={field.handleBlur}
                    placeholder={i18n.auth.newPassword}
                  />
                </InputContainer>
              </Row>
            )}
          </codeForm.Field>

          <codeForm.Field name="password_confirmation">
            {(field) => (
              <Row>
                <InputContainer
                  error={
                    shouldShowFieldError(field.state.meta, codeForm.state)
                      ? getFieldErrorFromMeta(field.state.meta)
                      : undefined
                  }
                >
                  <Input
                    id="forgot-password_confirmation"
                    type="password"
                    name="password_confirmation"
                    autoComplete="new-password"
                    value={field.state.value}
                    onKeyDown={handleEnterCode}
                    onChange={(value) => field.handleChange(typeof value === 'string' ? value : '')}
                    onBlur={field.handleBlur}
                    placeholder={i18n.auth.newPasswordRepeat}
                  />
                </InputContainer>
              </Row>
            )}
          </codeForm.Field>

          <GreyText>{i18n.auth.issue}</GreyText>
          <Row>
            <Button wide id="forgot-submit" onClick={handleCodeFormSubmit} disabled={submitted}>
              {i18n.auth.save}
            </Button>
          </Row>
        </Container>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Container>
        <emailForm.Field name="email">
          {(field) => (
            <Row>
              <InputContainer
                error={
                  shouldShowFieldError(field.state.meta, emailForm.state)
                    ? getFieldErrorFromMeta(field.state.meta)
                    : undefined
                }
              >
                <Input
                  id="forgot-email"
                  name="email"
                  value={field.state.value}
                  onKeyDown={handleEnterEmail}
                  onChange={(value) => {
                    const str = typeof value === 'string' ? value : '';
                    localStorage.setItem(LocalStorageHelper.AUTH_EMAIL, str);
                    field.handleChange(str);
                  }}
                  onBlur={field.handleBlur}
                  placeholder={i18n.auth?.email ?? 'Email'}
                />
              </InputContainer>
            </Row>
          )}
        </emailForm.Field>

        <Row>
          <Button wide id="forgot-submit" onClick={handleEmailSubmit} disabled={submitted}>
            {i18n.auth.restore}
          </Button>
        </Row>
      </Container>
    </Wrapper>
  );
}

export default Forgot;
